import { defineStore } from 'pinia';
import { ref } from 'vue';
import type {
  Account,
  CreateAccountRequest,
  CreateAccountResponse,
  CreateKeyResponse,
  UpdateAccountRequest,
  AccountType,
  AffiliationChangeReport,
} from '@/types/parley';
import * as api from '@/api/ParleyApi';
import { extractErrorMessage } from '@/common/utils/accountUtils';

export interface AccountQueryParams {
  page?: number;
  limit?: number;
  status?: string;
  ownerType?: string;
  fundingSource?: string;
  budgetLimit?: 'limited' | 'unlimited';
  createdFrom?: string;
  createdTo?: string;
  billingMode?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export const useAccountsStore = defineStore('accounts', () => {
  const accounts = ref<Account[]>([]);
  const selectedAccount = ref<Account | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  // Server-side pagination state
  const totalAccounts = ref(0);
  const currentPage = ref(1);
  const pageSize = ref(20);

  // ── List-view filter / sort state ───────────────────────────────────────
  // Held here (rather than in the page component) so it survives navigation:
  // opening an account and coming back keeps the user's search and filters.
  const searchTerm = ref('');
  const statusFilter = ref<string[]>([]);
  const selectedOwnerType = ref('');
  const selectedFundingSource = ref('');
  const budgetFilter = ref('');
  // PrimeVue range picker: [from, to]; the second slot is null mid-selection.
  const createdRange = ref<(Date | null)[] | null>(null);
  // Whether the "More filters" row is expanded.
  const showMore = ref(false);
  // Sort — PrimeVue uses 1 / -1; mapped to 'asc' / 'desc' when querying.
  const sortField = ref<string | undefined>(undefined);
  const sortOrder = ref<1 | -1 | undefined>(undefined);

  function resetFilters() {
    searchTerm.value = '';
    statusFilter.value = [];
    selectedOwnerType.value = '';
    selectedFundingSource.value = '';
    budgetFilter.value = '';
    createdRange.value = null;
  }

  // WEB-005: monotonic sequence so an older, slower list response can't clobber
  // the results of a newer query (typing in search, flipping a filter). Only the
  // latest request is allowed to commit state, set the error, or clear loading.
  let latestListSeq = 0;

  async function fetchAccounts(params?: AccountQueryParams) {
    const seq = ++latestListSeq;
    isLoading.value = true;
    error.value = null;
    try {
      const response = await api.listAccounts({
        page: params?.page ?? currentPage.value,
        limit: params?.limit ?? pageSize.value,
        status: params?.status,
        ownerType: params?.ownerType,
        fundingSource: params?.fundingSource,
        budgetLimit: params?.budgetLimit,
        createdFrom: params?.createdFrom,
        createdTo: params?.createdTo,
        billingMode: params?.billingMode,
        search: params?.search,
        sortBy: params?.sortBy,
        sortOrder: params?.sortOrder,
      });
      if (seq !== latestListSeq) return; // superseded by a newer query — discard
      accounts.value = response.data;
      totalAccounts.value = response.total;
      currentPage.value = response.page;
      pageSize.value = response.limit;
    } catch (e: unknown) {
      if (seq !== latestListSeq) return; // stale failure — a newer query is in flight
      error.value = extractErrorMessage(e, 'Failed to fetch accounts');
    } finally {
      if (seq === latestListSeq) isLoading.value = false;
    }
  }

  async function fetchAccount(accountId: string) {
    isLoading.value = true;
    error.value = null;
    try {
      selectedAccount.value = await api.getAccount(accountId);
    } catch (e: unknown) {
      error.value = extractErrorMessage(e, 'Failed to fetch account');
    } finally {
      isLoading.value = false;
    }
  }

  async function createAccount(
    request: CreateAccountRequest
  ): Promise<CreateAccountResponse | null> {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await api.createAccount(request);
      await fetchAccounts();
      return response;
    } catch (e: unknown) {
      error.value = extractErrorMessage(e, 'Failed to create account');
      return null;
    } finally {
      isLoading.value = false;
    }
  }

  async function updateAccount(accountId: string, request: UpdateAccountRequest): Promise<boolean> {
    isLoading.value = true;
    error.value = null;
    try {
      const updated = await api.updateAccount(accountId, request);
      selectedAccount.value = updated;
      const idx = accounts.value.findIndex((a) => a.accountId === accountId);
      if (idx !== -1) accounts.value[idx] = updated;
      return true;
    } catch (e: unknown) {
      error.value = extractErrorMessage(e, 'Failed to update account');
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  // Returns the consequences report on success (rather than a bare boolean) so
  // the caller can surface what the new affiliation implies — the API writes
  // accountType only and deliberately does not move allocations or credit.
  async function updateAccountAffiliation(
    accountId: string,
    accountType: AccountType
  ): Promise<AffiliationChangeReport | null> {
    isLoading.value = true;
    error.value = null;
    try {
      const result = await api.updateAccountAffiliation(accountId, accountType);
      selectedAccount.value = result.account;
      const idx = accounts.value.findIndex((a) => a.accountId === accountId);
      if (idx !== -1) accounts.value[idx] = result.account;
      return result.affiliationChange;
    } catch (e: unknown) {
      error.value = extractErrorMessage(e, 'Failed to update account affiliation');
      return null;
    } finally {
      isLoading.value = false;
    }
  }

  async function resetAccountFreeModelTokens(accountId: string): Promise<boolean> {
    isLoading.value = true;
    error.value = null;
    try {
      const updated = await api.resetAccountFreeModelTokens(accountId);
      selectedAccount.value = updated;
      const idx = accounts.value.findIndex((a) => a.accountId === accountId);
      if (idx !== -1) accounts.value[idx] = updated;
      return true;
    } catch (e: unknown) {
      error.value = extractErrorMessage(e, 'Failed to reset account free model tokens');
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  async function addAccountAdmin(accountId: string, krbName: string): Promise<Account | null> {
    error.value = null;
    try {
      const updated = await api.addAccountAdmin(accountId, krbName);
      selectedAccount.value = updated;
      const idx = accounts.value.findIndex((a) => a.accountId === accountId);
      if (idx !== -1) accounts.value[idx] = updated;
      return updated;
    } catch (e: unknown) {
      error.value = extractErrorMessage(e, 'Failed to add account admin');
      return null;
    }
  }

  async function removeAccountAdmin(accountId: string, krbName: string): Promise<Account | null> {
    error.value = null;
    try {
      const updated = await api.removeAccountAdmin(accountId, krbName);
      selectedAccount.value = updated;
      const idx = accounts.value.findIndex((a) => a.accountId === accountId);
      if (idx !== -1) accounts.value[idx] = updated;
      return updated;
    } catch (e: unknown) {
      error.value = extractErrorMessage(e, 'Failed to remove account admin');
      return null;
    }
  }

  async function deactivateAccount(accountId: string, reason: string): Promise<boolean> {
    isLoading.value = true;
    error.value = null;
    try {
      await api.deactivateAccount(accountId, reason);
      await fetchAccount(accountId);
      const idx = accounts.value.findIndex((a) => a.accountId === accountId);
      if (idx !== -1 && selectedAccount.value) accounts.value[idx] = selectedAccount.value;
      return true;
    } catch (e: unknown) {
      error.value = extractErrorMessage(e, 'Failed to deactivate account');
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  async function reactivateAccount(accountId: string): Promise<boolean> {
    isLoading.value = true;
    error.value = null;
    try {
      await api.reactivateAccount(accountId);
      await fetchAccount(accountId);
      const idx = accounts.value.findIndex((a) => a.accountId === accountId);
      if (idx !== -1 && selectedAccount.value) accounts.value[idx] = selectedAccount.value;
      return true;
    } catch (e: unknown) {
      error.value = extractErrorMessage(e, 'Failed to reactivate account');
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  async function createApiKey(
    accountId: string,
    krbName: string,
    label: string,
    monthlyBudgetLimit: number,
    monthlyBudgetLimitAlertThreshold: number
  ): Promise<CreateKeyResponse | null> {
    error.value = null;
    try {
      const response = await api.createKey(
        accountId,
        krbName,
        label,
        'never',
        monthlyBudgetLimit,
        monthlyBudgetLimitAlertThreshold
      );
      await fetchAccount(accountId);
      return response;
    } catch (e: unknown) {
      error.value = extractErrorMessage(e, 'Failed to create API key');
      return null;
    }
  }

  async function regenerateApiKey(
    accountId: string,
    krbName: string,
    label: string
  ): Promise<CreateKeyResponse | null> {
    error.value = null;
    try {
      const response = await api.regenerateKey(accountId, krbName, label);
      await fetchAccount(accountId);
      return response;
    } catch (e: unknown) {
      error.value = extractErrorMessage(e, 'Failed to regenerate API key');
      return null;
    }
  }

  return {
    accounts,
    selectedAccount,
    isLoading,
    error,
    totalAccounts,
    currentPage,
    pageSize,
    searchTerm,
    statusFilter,
    selectedOwnerType,
    selectedFundingSource,
    budgetFilter,
    createdRange,
    showMore,
    sortField,
    sortOrder,
    resetFilters,
    fetchAccounts,
    fetchAccount,
    createAccount,
    updateAccount,
    updateAccountAffiliation,
    deactivateAccount,
    reactivateAccount,
    createApiKey,
    regenerateApiKey,
    resetAccountFreeModelTokens,
    addAccountAdmin,
    removeAccountAdmin,
  };
});
